import {useEffect, useRef, useState, useCallback} from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {LoadingPage} from 'shells/loading-page/loading-page';
import {AccountCardCreateable} from 'shells/peep-cards/account-card/account-card-createable/account-card-createable';
import type {AccountInput} from 'shells/peep-cards/account-card/types';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {getPotentialAccounts} from '../../../account-hierarchy/utils';

import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {useCreateAccountForCoreEntity} from '../../../../core-entity/graphql/hooks/use-create-account-for-core-entity';
import {CompanyRow} from '../../../../core-entity/rows/peep-rows/company-row';
import {HOVERVIEW_ORIENTATION} from '../../../../core-entity/rows/peep-rows/types';
import {AccountHierarchyFeatureGateModal} from '../../../../limits/feature-gates/account-hierarchy-feature-gate-modal';

import {accountRefetchForEvent} from '../../hooks/use-account-update-refetch';

import {useAddAccountParentRelationship} from './graphql/hooks/use-add-account-parent-relationship';
import {useGetAccountHierarchyWidget} from './graphql/hooks/use-get-account-hierarchy-widget';
import {useRemoveAccountParentRelationship} from './graphql/hooks/use-remove-account-parent-relationship';
import {
    getAddAccountButtonProps,
    getEmptyStateProps,
    getRefetchQueries,
    getSecondaryUpgradeTitle,
} from './helpers';

import styles from './account-hierarchy-widget.module.css';

export type AddingAccountRelationship = 'parent' | 'child';

interface Props {
    widget: WidgetFragment;
    accountId: string;
}

export function AccountHierarchyWidget(props: Props) {
    const [relationshipToAdd, setRelationshipToAdd] = useState<AddingAccountRelationship | null>(
        null
    );
    const [error, setError] = useState<string | null | undefined>(null);
    const [isShowingUpgradeModal, setIsShowingUpgradeModal] = useState<boolean>(false);

    const {
        currentAccount,
        parentAccount,
        childAccounts,
        isLoading: isLoadingAccountHierarchy,
        refetch: refetchAccountHierarchy,
    } = useGetAccountHierarchyWidget(props.accountId, !NutClientConfig.hasAccountHierarchy);
    const {removeAccountParentRelationship, isLoading: isLoadingRemoveAccountParentRelationship} =
        useRemoveAccountParentRelationship(props.accountId);
    const {addAccountParentRelationship, isLoading: isLoadingAddAccountParentRelationship} =
        useAddAccountParentRelationship(props.accountId);
    const {createAccountForCoreEntity, isLoading: isLoadingCreateAccount} =
        useCreateAccountForCoreEntity({
            refetchQueries: getRefetchQueries(props.accountId),
        });

    const dispatchHierarchyUpdated = useCallback(() => {
        accountRefetchForEvent('accountHierarchyUpdated');
    }, []);

    const parentToShow = childAccounts?.length ? currentAccount : parentAccount;
    const childrenToShow = childAccounts?.length
        ? childAccounts
        : currentAccount
        ? [currentAccount]
        : [];

    const isEmpty = !(parentToShow && childrenToShow.length);
    const isLoading = isLoadingAccountHierarchy;
    const isRefetching =
        isLoadingRemoveAccountParentRelationship ||
        isLoadingAddAccountParentRelationship ||
        isLoadingCreateAccount;

    const parentCardRowRef = useRef<HTMLDivElement>(null);
    const [parentCardHeight, setParentCardHeight] = useState(24);

    // Used to calculate the grouping lines height
    useEffect(() => {
        if (parentCardRowRef.current) {
            const height = parentCardRowRef.current.offsetHeight;
            if (height > 0) {
                setParentCardHeight(height);
            }
        }
    }, [parentToShow?.id, childrenToShow.length]);

    useEffect(() => {
        const handleDuplicateStatusUpdate = () => {
            refetchAccountHierarchy();
        };

        $('body').on(Nut.events.card.duplicateStatusWasUpdated, handleDuplicateStatusUpdate);

        return () => {
            $('body').off(Nut.events.card.duplicateStatusWasUpdated, handleDuplicateStatusUpdate);
        };
    }, [refetchAccountHierarchy]);

    const handleRemoveAccountParentRelationship = (accountId: string) => {
        removeAccountParentRelationship({accountId})
            .then(() => {
                // Only trigger when removing a child account
                if (accountId !== props.accountId) {
                    dispatchHierarchyUpdated();
                }
            })
            .catch((error) => {
                setError(safelyGetGraphQLErrorMessage(error));
            });
    };

    const handleAddAccountParentRelationship = (selectedAccount: DisplayOptionEntity) => {
        const childAccountId =
            relationshipToAdd === 'parent' ? props.accountId : selectedAccount.id;
        const parentAccountId =
            relationshipToAdd === 'parent' ? selectedAccount.id : props.accountId;

        addAccountParentRelationship({accountId: childAccountId, parentAccountId})
            .then(() => {
                if (relationshipToAdd === 'child') {
                    dispatchHierarchyUpdated();
                }
            })
            .catch((error) => {
                setError(safelyGetGraphQLErrorMessage(error));
            });

        setRelationshipToAdd(null);
    };

    const handleCreateAccount = (input: AccountInput) => {
        const childAccountIds = relationshipToAdd === 'parent' ? [props.accountId] : undefined;
        const parentAccountId = relationshipToAdd === 'parent' ? undefined : props.accountId;

        createAccountForCoreEntity({
            name: input.name,
            description: input.description,
            ...(input.address && input.address.trim()
                ? {
                      addresses: [
                          {
                              name: 'address',
                              address: {},
                              singleLineAddress: input.address.trim(),
                          },
                      ],
                  }
                : undefined),
            parentAccountId,
            childAccountIds: childAccountIds,
        }).catch((error) => {
            setError(safelyGetGraphQLErrorMessage(error));
        });

        setRelationshipToAdd(null);
    };

    const emptyStateProps = getEmptyStateProps(isEmpty, Boolean(relationshipToAdd), isRefetching);
    const addButtonProps = getAddAccountButtonProps(
        setRelationshipToAdd,
        Boolean(parentAccount),
        Boolean(childAccounts?.length),
        isEmpty
    );
    const secondaryUpgradeTitle = getSecondaryUpgradeTitle(() => setIsShowingUpgradeModal(true));

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                secondaryTitle={secondaryUpgradeTitle}
                isLoading={isLoading}
                addButtonProps={addButtonProps}
                emptyStateProps={emptyStateProps}
                errorProps={{
                    error: error,
                    setError: setError,
                }}
                hasBorder={true}
            >
                <>
                    {isRefetching && <LoadingPage padding={8} />}
                    {relationshipToAdd && (
                        <AccountCardCreateable
                            onSelectAccount={handleAddAccountParentRelationship}
                            onSubmit={handleCreateAccount}
                            onClose={() => {
                                setRelationshipToAdd(null);
                            }}
                            onSearchPotentialAccounts={(searchInput: AccountInput) =>
                                getPotentialAccounts({
                                    accountInput: searchInput,
                                    isAddingParent: relationshipToAdd === 'parent',
                                    accountId: props.accountId,
                                    alreadyAddedAccounts:
                                        childAccounts?.map((childAccount) => childAccount.id) ?? [],
                                })
                            }
                            isPotentialAccountsPopover={true}
                        />
                    )}
                    {parentToShow && childrenToShow.length > 0 && (
                        <>
                            <div ref={parentCardRowRef}>
                                <CompanyRow
                                    key={parentToShow.id}
                                    accountId={parentToShow.id}
                                    name={parentToShow.name}
                                    avatarUrl={parentToShow.avatarUrl}
                                    accountUrl={parentToShow.htmlUrlPath}
                                    description={parentToShow.description}
                                    location={
                                        parentToShow.postalAddresses?.primary?.location
                                            ?.formattedAddress
                                    }
                                    parentEntityName='Accounts'
                                    orientationOverride={HOVERVIEW_ORIENTATION.WEST}
                                    isDuplicate={parentToShow.hasDuplicates}
                                    removeAccount={() =>
                                        handleRemoveAccountParentRelationship(props.accountId)
                                    }
                                    setError={setError}
                                    phoneCallerType={null}
                                    hasConnectedPhoneIntegration={false}
                                    phone={null}
                                    isParent={true}
                                    canRemove={parentToShow.id !== currentAccount?.id}
                                    deleteType='association'
                                />
                            </div>
                            <div className={styles.container}>
                                <div
                                    style={{
                                        marginTop: `${45 - parentCardHeight}px`,
                                    }}
                                    className={styles['grouping-line-container']}
                                >
                                    <div className={styles['left-grouping-line']} />
                                </div>
                                <div className={styles.content}>
                                    {childrenToShow.map((childAccount) => (
                                        <div key={childAccount.id} className='pl-12'>
                                            <CompanyRow
                                                accountId={childAccount.id}
                                                name={childAccount.name}
                                                avatarUrl={childAccount.avatarUrl}
                                                accountUrl={childAccount.htmlUrlPath}
                                                description={childAccount.description}
                                                location={
                                                    childAccount.postalAddresses?.primary?.location
                                                        ?.formattedAddress
                                                }
                                                parentEntityName='Accounts'
                                                orientationOverride={HOVERVIEW_ORIENTATION.WEST}
                                                isDuplicate={childAccount.hasDuplicates}
                                                removeAccount={
                                                    handleRemoveAccountParentRelationship
                                                }
                                                setError={setError}
                                                phoneCallerType={null}
                                                hasConnectedPhoneIntegration={false}
                                                phone={null}
                                                canRemove={childAccount.id !== currentAccount?.id}
                                                deleteType='association'
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </>
            </CollapsibleCardWidget>
            {isShowingUpgradeModal && (
                <AccountHierarchyFeatureGateModal
                    isOpen={isShowingUpgradeModal}
                    onClose={() => setIsShowingUpgradeModal(false)}
                />
            )}
        </>
    );
}
